import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { jobsAPI, dashboardAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import Pagination from '../components/Pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Plus,
  Search,
  Clock,
  MapPin,
  User,
  Building2,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  Repeat,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  AlertTriangle,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { format, parseISO, isPast, isToday } from 'date-fns';

export default function Jobs() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [divisionFilter, setDivisionFilter] = useState('all');
  const [divisions, setDivisions] = useState([]);
  const [deleteId, setDeleteId] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  
  // Sorting state
  const [sortBy, setSortBy] = useState('start_time');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Overdue pending jobs dialog
  const [overdueJob, setOverdueJob] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.roles?.includes('admin');
  const totalPages = Math.ceil(totalItems / pageSize);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        page_size: pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (divisionFilter !== 'all') {
        params.cost_center = divisionFilter;
      }
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      const response = await jobsAPI.getPaginated(params);
      const data = response.data;
      
      // Handle both paginated and array responses
      if (Array.isArray(data)) {
        setJobs(data);
        setTotalItems(data.length);
      } else {
        setJobs(data.items || []);
        setTotalItems(data.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, statusFilter, divisionFilter, searchQuery, sortBy, sortOrder]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Check for overdue pending jobs and show notification
  useEffect(() => {
    if (!loading && jobs.length > 0) {
      const overduePendingJobs = jobs.filter(job => 
        job.status === 'pending' && isPast(parseISO(job.start_time)) && !isToday(parseISO(job.start_time))
      );
      
      if (overduePendingJobs.length > 0) {
        toast.warning(
          `${overduePendingJobs.length} job${overduePendingJobs.length > 1 ? 's are' : ' is'} still pending but start date has passed`,
          {
            description: 'Click the warning icon to update the status',
            duration: 6000,
          }
        );
      }
    }
  }, [loading, jobs]);

  // Check if a job is overdue (pending with passed start date)
  const isJobOverdue = (job) => {
    return job.status === 'pending' && isPast(parseISO(job.start_time)) && !isToday(parseISO(job.start_time));
  };

  // Handle status update for overdue job
  const handleUpdateOverdueStatus = async (newStatus) => {
    if (!overdueJob) return;
    setUpdatingStatus(true);
    try {
      await jobsAPI.update(overdueJob.id, { status: newStatus });
      toast.success(`Job status updated to ${newStatus.replace('_', ' ')}`);
      fetchJobs();
    } catch (error) {
      toast.error('Failed to update job status');
    } finally {
      setUpdatingStatus(false);
      setOverdueJob(null);
    }
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, divisionFilter]);

  // Load division (cost center) options
  useEffect(() => {
    dashboardAPI.getCostCenters()
      .then((res) => setDivisions(res.data.cost_centers || []))
      .catch(() => {});
  }, []);

  // Handle sorting
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  // Get sort icon for column
  const getSortIcon = (field) => {
    if (sortBy !== field) {
      return <ArrowUpDown className="w-4 h-4 ml-1 opacity-50" />;
    }
    return sortOrder === 'asc' 
      ? <ArrowUp className="w-4 h-4 ml-1" />
      : <ArrowDown className="w-4 h-4 ml-1" />;
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  // Debounced search: reset to page 1 (fetch is triggered by the fetchJobs effect,
  // whose identity changes when searchQuery changes)
  useEffect(() => {
    const timeout = setTimeout(() => {
      setCurrentPage((p) => (p !== 1 ? 1 : p));
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await jobsAPI.delete(deleteId);
      toast.success('Job deleted successfully');
      fetchJobs();
    } catch (error) {
      toast.error('Failed to delete job');
    } finally {
      setDeleteId(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: 'status-pending',
      in_progress: 'status-in_progress',
      completed: 'status-completed',
      cancelled: 'status-cancelled',
    };
    return statusClasses[status] || 'bg-gray-100 text-gray-800';
  };

  // Client-side filtering only for search (server handles status filter)
  const filteredJobs = jobs;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="jobs-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold">Jobs</h1>
          <p className="text-muted-foreground mt-1">Manage job assignments and schedules</p>
        </div>
        {isAdmin && (
          <Button onClick={() => navigate('/jobs/new')} data-testid="create-job-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Job
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search jobs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="archived">Archived (Completed + Cancelled)</SelectItem>
                <SelectItem value="all">All Statuses</SelectItem>
              </SelectContent>
            </Select>
            <Select value={divisionFilter} onValueChange={setDivisionFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="division-filter">
                <SelectValue placeholder="Filter by division" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Divisions</SelectItem>
                {divisions.map((d) => (
                  <SelectItem key={d} value={d}>
                    {d}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Jobs Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-muted-foreground">No jobs found</p>
              {isAdmin && (
                <Button className="mt-4" onClick={() => navigate('/jobs/new')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create your first job
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <button 
                      onClick={() => handleSort('title')} 
                      className="flex items-center hover:text-foreground transition-colors"
                      data-testid="sort-title"
                    >
                      Job
                      {getSortIcon('title')}
                    </button>
                  </TableHead>
                  <TableHead className="hidden md:table-cell">Customer</TableHead>
                  <TableHead className="hidden lg:table-cell">Assigned To</TableHead>
                  <TableHead>
                    <button 
                      onClick={() => handleSort('start_time')} 
                      className="flex items-center hover:text-foreground transition-colors"
                      data-testid="sort-date"
                    >
                      Date & Time
                      {getSortIcon('start_time')}
                    </button>
                  </TableHead>
                  <TableHead>
                    <button 
                      onClick={() => handleSort('status')} 
                      className="flex items-center hover:text-foreground transition-colors"
                      data-testid="sort-status"
                    >
                      Status
                      {getSortIcon('status')}
                    </button>
                  </TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredJobs.map((job) => (
                  <TableRow key={job.id} className={`table-row-hover ${isJobOverdue(job) ? 'bg-amber-50 dark:bg-amber-950/20' : ''}`} data-testid={`job-row-${job.id}`}>
                    <TableCell>
                      <div>
                        <div className="flex items-center gap-2">
                          {isJobOverdue(job) && (
                            <button
                              onClick={() => setOverdueJob(job)}
                              className="text-amber-600 hover:text-amber-700 transition-colors"
                              title="Job start date has passed - Click to update status"
                              data-testid={`overdue-warning-${job.id}`}
                            >
                              <AlertTriangle className="w-4 h-4" />
                            </button>
                          )}
                          <p className="font-medium">{job.title}</p>
                          {job.job_number && (
                            <span className="text-xs text-muted-foreground font-mono bg-muted px-1.5 py-0.5 rounded" data-testid={`job-number-${job.id}`}>
                              {job.job_number}
                            </span>
                          )}
                          {(job.is_recurring || job.is_recurring_instance) && (
                            <span className="inline-flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded" title="Recurring job">
                              <Repeat className="w-3 h-3" />
                            </span>
                          )}
                        </div>
                        {job.location && (
                          <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5">
                            <MapPin className="w-3 h-3" />
                            {job.location}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {job.customer_name ? (
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-muted-foreground" />
                          {job.customer_name}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {job.assigned_names?.length > 0 ? (
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <span className="truncate max-w-[150px]">
                            {job.assigned_names.join(', ')}
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">Unassigned</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm">{format(parseISO(job.start_time), 'MMM d, yyyy')}</p>
                          <p className="text-xs text-muted-foreground">
                            {format(parseISO(job.start_time), 'h:mm a')} - {format(parseISO(job.end_time), 'h:mm a')}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`badge-status ${getStatusBadge(job.status)}`}>
                        {job.status.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`job-menu-${job.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={`/jobs/${job.id}`}>
                              <Eye className="w-4 h-4 mr-2" />
                              View
                            </Link>
                          </DropdownMenuItem>
                          {isAdmin && (
                            <>
                              <DropdownMenuItem asChild>
                                <Link to={`/jobs/${job.id}/edit`}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit
                                </Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={() => setDeleteId(job.id)}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          
          {/* Pagination */}
          {!loading && totalItems > 0 && (
            <div className="border-t px-4">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Job</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this job? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Overdue Job Status Update Dialog */}
      <AlertDialog open={!!overdueJob} onOpenChange={() => setOverdueJob(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Job Start Date Has Passed
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  The job <strong>"{overdueJob?.title}"</strong> is still marked as <Badge className="badge-status status-pending mx-1">pending</Badge> but was scheduled to start on{' '}
                  <strong>{overdueJob && format(parseISO(overdueJob.start_time), 'MMM d, yyyy')}</strong>.
                </p>
                <p>Would you like to update the status?</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel disabled={updatingStatus}>Keep as Pending</AlertDialogCancel>
            <Button 
              variant="outline"
              onClick={() => handleUpdateOverdueStatus('cancelled')}
              disabled={updatingStatus}
              className="border-red-200 text-red-600 hover:bg-red-50"
            >
              Mark as Cancelled
            </Button>
            <Button 
              onClick={() => handleUpdateOverdueStatus('in_progress')}
              disabled={updatingStatus}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {updatingStatus ? 'Updating...' : 'Mark as In Progress'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
